import { useState, useCallback } from "react"
import { Button, Card, CardBody } from "reactstrap"
import { useQueryClient } from "@tanstack/react-query"
import VehicleContentsModal from "./VehicleContentsModal"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { VEHICLE_STOCK_QUERY_KEY } from "hooks/stockHook"

export default function VehicleContentsTab({ stock }) {
  const [modal, setModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const queryClient = useQueryClient()

  const publishes = stock?.publishes || []
  const totalContents = publishes.reduce(
    (acc, pub) => acc + (pub?.contents?.length || 0),
    0
  )

  const vehicleStockId = stock?.id

  const handleOpenModal = useCallback(vehicle => {
    setSelectedData({ ...vehicle, vehicle_stock_id: stock?.id })
    setModal(true)
  }, [stock?.id])

  const handleCloseModal = useCallback(() => {
    setModal(false)
    setSelectedData(null)
  }, [])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    await queryClient.invalidateQueries({
      queryKey: [VEHICLE_STOCK_QUERY_KEY, vehicleStockId],
    })
    setRefreshing(false)
  }, [queryClient, vehicleStockId])

  const handleContentChange = useCallback(async () => {
    await queryClient.refetchQueries({
      queryKey: [VEHICLE_STOCK_QUERY_KEY],
      refetchType: "all",
    })
  }, [queryClient])

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0 fw-semibold">Associated Contents</h5>
            <div className="d-flex align-items-center gap-3">
              <div className="text-muted small">
                Platforms: {publishes.length} | Total Contents: {totalContents}
              </div>
              <CanView access={[ACCESS.VEHICLE_CONTENT.VIEW_VEHICLE_CONTENT_LIST]}>
                <Button
                  color="primary"
                  size="sm"
                  disabled={!stock?.id}
                  onClick={() => handleOpenModal(stock)}
                >
                  <i className="bx bx-list-ul me-1" />
                  View Contents
                </Button>
              </CanView>
              <ButtonTooltip
                type="refresh"
                loading={refreshing}
                btnId="refresh-contents"
                onClick={handleRefresh}
              />
            </div>
          </div>

          {publishes.length === 0 ? (
            <div className="text-center text-muted py-4">
              No content published on any platform
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Contents</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {publishes.map(pub => (
                    <tr key={pub?.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {pub?.platform?.icon && (
                            <i className={pub.platform.icon} />
                          )}
                          <span>{pub?.platform?.name || "-"}</span>
                        </div>
                      </td>
                      <td>{pub?.contents?.length || 0}</td>
                      <td>
                        {pub?.contents?.some(c => c?.is_published) ? (
                          <span className="badge bg-success">Published</span>
                        ) : (
                          <span className="badge bg-warning">Pending</span>
                        )}
                      </td>
                      <td>
                        <CanView access={[ACCESS.VEHICLE_CONTENT.VIEW_VEHICLE_CONTENT_LIST]}>
                          <ButtonTooltip
                            type="view"
                            btnId={`view-contents-${pub?.id}`}
                            onClick={() => handleOpenModal({ ...pub, stock_id: stock?.stock_id, vehicle_stock_id: stock?.id })}
                          />
                        </CanView>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <VehicleContentsModal
        modal={modal}
        toggle={handleCloseModal}
        selectedData={selectedData}
        onContentChange={handleContentChange}
      />
    </>
  )
}